
  import { defineComponent, onMounted, ref } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter, useRoute } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import MonacoEditor from '@guolao/vue-monaco-editor';
  import moment from 'moment';

  export default defineComponent({
    name: 'newsletter-sections-market-highlights-editing',
    components: {
      ErrorMessage,
      Field,
      Form,
      MonacoEditor,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const { can } = useAbility();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const submitPullFirstButton = ref<HTMLButtonElement | null>(null);
      const submitPullSecondButton = ref<HTMLButtonElement | null>(null);
      const shortedDescription1 = ref<string>('');
      const shortedDescription2 = ref<string>('');

      const variablesContent = ref('{}');
      let news1 = ref({
        url: '',
        link1: '',
        image: '',
        title: '',
        description: '',
        section: '',
      });
      let news2 = ref({
        url: '',
        link2: '',
        image: '',
        title: '',
        description: '',
        section: '',
      });

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('newslettersSectionUpdating'), [
          translate('assets'),
        ]);
      });
      const { data } = await store.dispatch(
        Actions.GET_NEWSLETTER,
        route.params.id
      );

      variablesContent.value = data.data.variables;

      const translate = (text: string) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const newsletter = ref<any>(data.data);

      let htmlContentPreview = '';
      if (newsletter.value.variables) {
        const parsedVariables = JSON.parse(newsletter.value.variables);

        console.log('Parsed Variables:', parsedVariables);

        news1.value = {
          url: parsedVariables.link1 || '',
          link1: parsedVariables.link1 || '',
          image: parsedVariables.image1 || '',
          title: parsedVariables.title1 || '',
          description: parsedVariables.description1 || '',
          section: parsedVariables.section1 || '',
        };
        news2.value = {
          url: parsedVariables.link2 || '',
          link2: parsedVariables.link2 || '',
          image: parsedVariables.image2 || '',
          title: parsedVariables.title2 || '',
          description: parsedVariables.description2 || '',
          section: parsedVariables.section2 || '',
        };

        htmlContentPreview = newsletter.value.htmlContent.replace(
          /{{\s*([^}]+)\s*}}/g,
          (_, variable) => parsedVariables[variable.trim()] || ''
        );
      }

      const newslettersSchema = Yup.object().shape({
        title: Yup.string()
          .required(() => translate('EN_TITLE_IS_REQUIRED_FIELD'))
          .label('title'),
        key: Yup.string()
          .required(() => translate('EN_KEY_IS_REQUIRED_FIELD'))
          .label('key'),
      });

      const onSubmitPullFirstArticle = async (values) => {
        if (submitPullFirstButton.value) {
          // eslint-disable-next-line
        submitPullFirstButton.value!.disabled = true;
          submitPullFirstButton.value.setAttribute('data-kt-indicator', 'on');
        }
        const data = await store.dispatch(Actions.PULL_URL_NEWS, {
          url: values.link1,
        });
        data.date = moment(data.date).format('YYYY-MM-DD');
        news1.value = data;
        if (!news1.value) {
          return;
        }

        if (news1.value.description.length > 100) {
          news1.value.description =
            news1.value.description.substring(0, 200) + '...';
        }

        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitPullFirstButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
        submitPullFirstButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_PULLED_DATA'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            //Deactivate indicator
            submitPullFirstButton.value?.removeAttribute('data-kt-indicator');
            // eslint-disable-next-line
          submitPullFirstButton.value!.disabled = false;
          });
        }
      };
      const onSubmitPullSecondArticle = async (values) => {
        if (submitPullSecondButton.value) {
          // eslint-disable-next-line
        submitPullSecondButton.value!.disabled = true;
          submitPullSecondButton.value.setAttribute('data-kt-indicator', 'on');
        }
        const data = await store.dispatch(Actions.PULL_URL_NEWS, {
          url: values.link2,
        });
        data.date = moment(data.date).format('YYYY-MM-DD');
        news2.value = data;
        if (!news2.value) {
          return;
        }

        if (news2.value.description.length > 200) {
          news2.value.description =
            news2.value.description.substring(0, 200) + '...';
        }

        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitPullSecondButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
        submitPullSecondButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_PULLED_DATA'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            //Deactivate indicator
            submitPullSecondButton.value?.removeAttribute('data-kt-indicator');
            // eslint-disable-next-line
          submitPullSecondButton.value!.disabled = false;
          });
        }
      };

      const onSubmitUpdate = async (values) => {
        if (submitButton.value) {
          // eslint-disable-next-line
        submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }
        if (values.sort) {
          values.sort = Number(values.sort);
        }

        const variables = {
          link1: newsletter.value.link1 || news1.value.url || '',
          link2: newsletter.value.link2 || news2.value.url || '',
          badge1: '',
          badge2: '',
          image1: news1.value.image || '',
          image2: news2.value.image || '',
          title1: news1.value.title || '',
          title2: news2.value.title || '',
          description1: news1.value.description || '',
          description2: news2.value.description || '',
          section1: news1.value.section || '',
          section2: news2.value.section || '',
        };

        const payload = {
          ...values,
          htmlContent: newsletter.value.htmlContent,
          variables,
        };
        await store.dispatch(Actions.UPDATE_NEWSLETTER, {
          id: route.params.id,
          data: payload,
        });
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
        submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_NEWSLETTER_SECTION'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'newsletter-sections-listing' });
          });
        }
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        onSubmitUpdate,
        submitButton,
        translate,
        newslettersSchema,
        goBack,
        newsletter,
        can,
        variablesContent,
        news1,
        news2,
        submitPullFirstButton,
        submitPullSecondButton,
        onSubmitPullFirstArticle,
        onSubmitPullSecondArticle,
        htmlContentPreview,
      };
    },
  });
